// This file was copied from site scanner. Planning to move it to ui-common.
import { keyBy } from 'lodash-es';

import { OptionType } from '@evinced-private/ui-common';

import {
	addAttachmentToPayload,
	dataURItoBlob,
	getClassificationNodes,
	getFieldsMetaData,
	getFormattedFields,
	getOrganizationNameUrl,
	getPaths,
	getProjectNameUrl,
	getProjectsOptionType,
	getProjectTypes,
	getUsersUrl,
	openUrlInNewTab
} from 'src/common/components/azure-issue-creator/Helpers/AzureHelper';
import { AZURE_URL_PATHS } from 'src/common/components/azure-issue-creator/Helpers/UrlPaths';

import {
	IAzureClassificationNode,
	IAzureField,
	IAzureFieldType,
	IAzureOrganization,
	IAzureProject,
	IAzureProjectType,
	IAzureUser
} from '../../types/AzureTypes';
import { ITokenData } from '../../types/JiraTypes';
import { Dictionary } from '../../types/LodashTypes';
import { getEvincedUserId } from '../AuthenticationService';
import { Logger } from '../Logger';

import { getAzureAccessToken, setAzureAccessToken } from './LocalStorageApi';

const {
	API_VERSION,
	WORK_ITEM_FIELDS,
	WORK_ITEM_TYPE,
	PROJECTS,
	WORK_ITEMS,
	PROJECT_TYPES,
	USERS,
	BYPASS,
	FIELDS,
	CLASSIFICATIONS,
	USER,
	ATTACHMENT
} = AZURE_URL_PATHS;

const azureAuthURL = process.env.AZURE_AUTH_TOKEN_URL as string;
const clientID = process.env.AZURE_APP_CLIENT_ID as string;
const clientSecret = process.env.AZURE_APP_CLIENT_SECRET as string;
const redirectURL = process.env.AZURE_REDIRECT_URL as string;
const scopes = 'vso.graph vso.identity vso.work_full';

export const getAzureAuthURL = (): string => {
	const userID = getEvincedUserId();
	// used for 'state' param (required for security) - a value that is associated with the user
	// that is directed to the authorization URL
	const state = userID;
	return `https://app.vssps.visualstudio.com/oauth2/authorize?client_id=${clientID}&response_type=Assertion&state=${state}&scope=${scopes}&redirect_uri=${redirectURL}`;
};
const innerCallApi = async (
	url: string,
	method = 'GET',
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	payload?: any,
	withToken = true,
	contentType = 'application/json-patch+json'
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
	const requestHeaders: HeadersInit = new Headers();

	requestHeaders.set('Content-Type', contentType);
	requestHeaders.set('Accept', 'application/json-patch+json');

	if (withToken) {
		const azureToken: ITokenData = JSON.parse(getAzureAccessToken());
		if (azureToken?.access_token) {
			requestHeaders.set('Authorization', `Bearer ${azureToken.access_token}`);
		} else {
			return Promise.reject(new Error('Unauthorized'));
		}
	}

	const fetchOptions: RequestInit = {
		mode: 'cors',
		method,
		headers: requestHeaders,
		body: null
	};

	if (payload) {
		fetchOptions.body =
			contentType === 'application/json-patch+json' ? JSON.stringify(payload) : payload;
	}
	Logger.debug('request started: ', url);
	try {
		let response = await fetch(url, fetchOptions);

		if (response.status === 203) {
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			await refreshAzureToken();
			response = await innerCallApi(url, method, payload, withToken, contentType);
		}
		if (!response.ok) {
			Logger.error('cannot call API', url, fetchOptions, response);
			throw response.status;
		}
		Logger.debug('request complete successfully: ', url);
		const json = await response.json();
		Logger.debug('Response for ', url, 'is:', json);
		return json;
	} catch (err) {
		Logger.error('error for request: ', url, ' with options: ', fetchOptions, 'Error', err);
		throw err;
	}
};

export const getAndSaveAzureToken = async (code: string): Promise<void> => {
	const azureToken = getAzureAccessToken();
	if (azureToken) {
		window.close();
		return;
	}

	const payload = `client_assertion_type=urn:ietf:params:oauth:client-assertion-type:jwt-bearer&client_assertion=${clientSecret}&grant_type=urn:ietf:params:oauth:grant-type:jwt-bearer&assertion=${code}&redirect_uri=${redirectURL}`;
	const data = await innerCallApi(
		azureAuthURL,
		'POST',
		payload,
		false,
		'application/x-www-form-urlencoded'
	);

	if (data.access_token) {
		setAzureAccessToken(JSON.stringify(data));
		window.close();
	}
};

const refreshAzureToken = async (): Promise<void> => {
	const azureToken = JSON.parse(getAzureAccessToken());

	if (azureToken) {
		const payload = `client_assertion_type=urn:ietf:params:oauth:client-assertion-type:jwt-bearer&client_assertion=${clientSecret}&grant_type=refresh_token&assertion=${azureToken.refresh_token}&redirect_uri=${redirectURL}`;
		const data = await innerCallApi(
			azureAuthURL,
			'POST',
			payload,
			false,
			'application/x-www-form-urlencoded'
		);

		if (data.access_token) {
			setAzureAccessToken(JSON.stringify(data));
		}
	}
};

export const getProjects = async (host: string): Promise<IAzureProject[]> => {
	const url = getOrganizationNameUrl(host);
	try {
		const projects = await innerCallApi(`${url}${PROJECTS}?${API_VERSION}`);
		return getProjectsOptionType(projects.value) ?? [];
	} catch (error) {
		Logger.error('error for request: getProjects', url, 'Error', error);
		return [];
	}
};

export const getUsers = async (host: string): Promise<IAzureUser[]> => {
	const url = getUsersUrl(host);

	try {
		const response = (await innerCallApi(`${url}${USERS}`)).value;
		const users = response.filter((user) => {
			if (!user.displayName.includes('Build Service')) {
				return {
					id: user.originId,
					email: user.mailAddress,
					displayName: user.displayName
				};
			}
			return null;
		});

		return users;
	} catch (error) {
		Logger.error('error for request: getUsers', url, 'Error', error);
		return [];
	}
};

const getClassification = async (host: string): Promise<IAzureClassificationNode[]> => {
	const url = `${host}/${CLASSIFICATIONS}&${API_VERSION}`;
	try {
		const classification = (await innerCallApi(url)).value;
		return classification;
	} catch (error) {
		Logger.error('error for request: getClassification', url, 'Error', error);
		return [];
	}
};

const getSelectedIssueType = async (
	host: string,
	selectedIssueType: string
): Promise<IAzureFieldType[]> => {
	const url = `${host}${WORK_ITEM_TYPE}/${selectedIssueType}${WORK_ITEM_FIELDS}&${API_VERSION}`;
	try {
		const selectedIssueType: IAzureFieldType[] = (await innerCallApi(url)).value;
		return selectedIssueType;
	} catch (error) {
		Logger.error('error for request: selectedIssueType', url, 'Error', error);
		return [];
	}
};

export const getProjectFields = async (
	host: string,
	selectedIssueType: string
): Promise<Dictionary<IAzureFieldType>> => {
	const fieldsUrl = `${host}${FIELDS}?${API_VERSION}`;
	try {
		let selectedIssueTypeFields: IAzureFieldType[] = await getSelectedIssueType(
			host,
			selectedIssueType
		);
		const fields: Dictionary<IAzureField> = keyBy(
			(await innerCallApi(fieldsUrl)).value,
			'referenceName'
		);
		const users: string[] = (await getUsers(host)).map((user: IAzureUser) => user.displayName);
		const classificationNodes: IAzureClassificationNode[] = await getClassification(host);
		const areaNodes: IAzureClassificationNode[] = [classificationNodes[0]];
		const iterationsNodes: IAzureClassificationNode[] = [classificationNodes[1]];
		const areas: string[] = getClassificationNodes(areaNodes);
		const iterations: string[] = getClassificationNodes(iterationsNodes);

		selectedIssueTypeFields = selectedIssueTypeFields.map((field: IAzureFieldType) => {
			if (field.referenceName === 'System.AreaPath') {
				return { ...field, allowedValues: areas, type: 'select' };
			}
			if (field.referenceName === 'System.IterationPath') {
				return { ...field, allowedValues: iterations, type: 'select' };
			}
			return fields[field.referenceName]?.isIdentity ? { ...field, allowedValues: users } : field;
		});

		return getFormattedFields(fields, selectedIssueTypeFields);
	} catch (error) {
		Logger.error('error for request: getProjectFields', fieldsUrl, 'Error', error);
		return keyBy([]);
	}
};

export const getUser = async (): Promise<IAzureUser> => {
	try {
		const response = await innerCallApi(USER);
		const user = {
			id: response.id,
			email: response.emailAddress,
			displayName: response.displayName
		};
		return user;
	} catch (error) {
		Logger.error('error for request: getUser', USER, 'Error', error);
		return {
			id: '',
			email: '',
			displayName: ''
		};
	}
};

export const getOrganizations = async (): Promise<OptionType[]> => {
	try {
		const { id } = await getUser();
		const organizations: IAzureOrganization[] = (
			await innerCallApi(
				`https://app.vssps.visualstudio.com/_apis/accounts?memberId=${id}&api-version=6.0`
			)
		).value;
		return organizations.map((organization) => {
			const paths = getPaths(organization.accountUri);
			const organizationName = paths[paths.length - 2];
			return { label: organizationName, value: organizationName };
		});
	} catch (error) {
		Logger.error('error for request: getOrganizations', 'Error', error);
		return null;
	}
};

export const getProjectTypesFromServer = async (host: string): Promise<OptionType[]> => {
	const url: string = getProjectNameUrl(host);
	try {
		const projectTypes: IAzureProjectType[] = (
			await innerCallApi(`${url}${PROJECT_TYPES}?${API_VERSION}`)
		).value;
		return getProjectTypes(projectTypes) ?? null;
	} catch (error) {
		Logger.error('error for request: getProjectTypes', url, 'Error', error);
		return [];
	}
};

const openAzureTicket = (url: string): void => {
	openUrlInNewTab(url);
};

export const callCreateIssue = async (
	host: string,
	selectedIssueType: string,
	screenshotUrlBase64: string,
	fields: Dictionary<IAzureFieldType>
): Promise<boolean> => {
	const attachmentHost: string = getProjectNameUrl(host);

	try {
		let payload = getFieldsMetaData(fields);

		if (screenshotUrlBase64) {
			const attachmentFile = dataURItoBlob(screenshotUrlBase64);
			const attachment = await innerCallApi(
				`${attachmentHost}${ATTACHMENT}&${API_VERSION}`,
				'POST',
				attachmentFile,
				true,
				'application/octet-stream'
			);

			payload = addAttachmentToPayload(attachment.url, payload);
		}
		const response = await innerCallApi(
			`${attachmentHost}${WORK_ITEMS}$${selectedIssueType}${BYPASS}&${API_VERSION}`,
			'POST',
			payload
		);

		if (response._links.html.href) {
			openAzureTicket(response._links.html.href);
		}
		return !!response;
	} catch (error) {
		Logger.error('error for request: createIssue', attachmentHost, 'Error', error);
		return false;
	}
};
